import { EnvironmentProviders, inject } from '@angular/core';
import {
    StorageTemporaryLinkConfig,
    provideStorageTemporaryLinkConfig,
} from '@interacta-shared/data-access-storage-temporary-link';
import { shellResourceIdentifierFn } from './shell-resource-identifier-fn';
import { ShellTemporaryLinkService } from './shell-temporary-link.service';

/**
 * Factory function that creates the storage temporary link configuration for the shell application.
 *
 * This factory combines the shell-specific resource identifier function with the
 * shell temporary link service to provide a complete configuration for the
 * shared temporary link system.
 *
 * @returns StorageTemporaryLinkConfig configured for shell application
 */
const storageTemporaryLinkConfigFactory = (): StorageTemporaryLinkConfig => {
    const shellTemporaryLinkService = inject(ShellTemporaryLinkService);

    return {
        resourceIdentifierFn: shellResourceIdentifierFn,
        getNewTemporaryLinkFn:
            shellTemporaryLinkService.getNewTemporaryLink.bind(
                shellTemporaryLinkService,
            ),
    };
};

/**
 * Provides shell-specific configuration for the storage temporary link system.
 *
 * This function sets up the dependency injection configuration specifically for
 * the shell application, using shell-specific implementations for URL parsing
 * and link generation.
 *
 * @returns EnvironmentProviders for Angular dependency injection
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     // ... other providers
 *     provideShellTemporaryLinkConfig(),
 *   ]
 * };
 * ```
 */
export function provideShellTemporaryLinkConfig(): EnvironmentProviders {
    return provideStorageTemporaryLinkConfig(storageTemporaryLinkConfigFactory);
}
